/**
 * Platform admin: SCF dataset management.
 *
 * - List loaded versions, mark active.
 * - Trigger a load of the bundled seed dataset (idempotent).
 *
 * Full SCF Excel imports are not exposed here; that runs from a CLI
 * loader the platform team operates against the Aurora cluster directly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AdminShell } from './admin-shell';
import { cn } from '@/lib/utils';

interface VersionRow {
  version: string;
  releasedAt: string | null;
  importedAt: string;
  notes: string | null;
  isActive: boolean;
}

export function AdminScfPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'scf', 'versions'],
    queryFn: () => apiClient.adminListScfVersions(),
  });
  const versions = (data?.data as VersionRow[] | undefined) ?? [];

  const load = useMutation({
    mutationFn: () => apiClient.adminLoadScf(),
    onSuccess: (res) => {
      const d = res.data as { version: string; controls: number; mappings: number; templateMappings: number };
      toast.success(`Loaded SCF ${d.version} — ${d.controls} controls, ${d.mappings} mappings, ${d.templateMappings} template links`);
      qc.invalidateQueries({ queryKey: ['admin', 'scf'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const activate = useMutation({
    mutationFn: (version: string) => apiClient.adminActivateScfVersion(version),
    onSuccess: (_res, version) => {
      toast.success(`SCF ${version} is now active`);
      qc.invalidateQueries({ queryKey: ['admin', 'scf'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <AdminShell subtitle="Load and pin SCF dataset versions. Org TEP snapshots use whichever version is active (or the org's pinned version).">

      <div className="flex items-center gap-3">
        <Database className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">SCF Dataset</h1>
      </div>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold mb-1">Bundled seed dataset</p>
            <p className="text-xs text-muted-foreground max-w-2xl">
              Loads the curated SCF subset shipped with this build (controls, framework mappings, finding-template links). Idempotent — safe to re-run after deploys.
            </p>
          </div>
          <Button onClick={() => load.mutate()} disabled={load.isPending}>
            {load.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Load / refresh
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-semibold">Loaded versions</p>
        </div>
        {isLoading && <p className="px-5 py-6 text-sm text-muted-foreground text-center">Loading…</p>}
        {!isLoading && versions.length === 0 && (
          <p className="px-5 py-8 text-sm text-muted-foreground text-center">
            No SCF versions loaded yet. Click "Load / refresh" above.
          </p>
        )}
        <div className="divide-y divide-border">
          {versions.map((v) => (
            <div key={v.version} className="px-5 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-mono font-bold text-foreground">{v.version}</p>
                <p className="text-xs text-muted-foreground">
                  imported {new Date(v.importedAt).toLocaleString()}
                  {v.releasedAt && <> · released {new Date(v.releasedAt).toLocaleDateString()}</>}
                </p>
                {v.notes && <p className="text-[11px] text-muted-foreground mt-1 max-w-xl">{v.notes}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {v.isActive ? (
                  <span className={cn('inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-india-green/15 text-india-green')}>
                    <CheckCircle2 className="h-3 w-3" /> active
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => activate.mutate(v.version)}
                    disabled={activate.isPending}
                  >
                    {activate.isPending && activate.variables === v.version ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : null}
                    Activate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </AdminShell>
  );
}
